import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Upload, Download, Eye, Palette, Image, Type, Signature, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CertificateTemplate {
  id: string;
  name: string;
  description: string;
  template_data: any;
  background_type?: string;
  background_value?: string;
  logo_url?: string;
  is_active: boolean;
}

interface CertificateDesignerProps {
  template: CertificateTemplate;
  onSave: (template: CertificateTemplate) => void;
  onCancel: () => void;
}

const DEFAULT_TEMPLATE_DATA = {
  title: "Certificate of Completion",
  subtitle: "Professional Development",
  layout: "centered",
  fonts: {
    title: { family: "serif", size: 32, weight: "bold", color: "#1e40af" },
    subtitle: { family: "sans-serif", size: 18, weight: "normal", color: "#6b7280" },
    participant: { family: "serif", size: 28, weight: "bold", color: "#111827" },
    course: { family: "sans-serif", size: 20, weight: "normal", color: "#374151" },
    details: { family: "sans-serif", size: 14, weight: "normal", color: "#6b7280" }
  },
  border: { 
    enabled: true, 
    color: "#1e40af", 
    width: 4, 
    style: "solid",
    cornerRadius: 8
  },
  watermark: {
    enabled: false,
    image: "",
    opacity: 0.1,
    position: "center"
  },
  logo: {
    enabled: false,
    url: "",
    position: "top-left",
    size: 80
  },
  signatures: {
    enabled: true,
    instructor: { enabled: true, label: "Instructor Signature", position: "bottom-left" },
    admin: { enabled: true, label: "Administrator", position: "bottom-right" }
  },
  dates: {
    completion: { enabled: true, label: "Date Completed" },
    issued: { enabled: true, label: "Date Issued" }
  },
  pdHours: {
    enabled: true,
    label: "Professional Development Hours",
    position: "bottom-center"
  },
  spacing: { padding: 40, sections: 20 }
};

export function CertificateDesigner({ template, onSave, onCancel }: CertificateDesignerProps) {
  const [templateData, setTemplateData] = useState(template.template_data || DEFAULT_TEMPLATE_DATA);
  const [backgroundType, setBackgroundType] = useState(template.background_type || 'solid');
  const [backgroundValue, setBackgroundValue] = useState(template.background_value || '#ffffff');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const watermarkInputRef = useRef<HTMLInputElement>(null);

  const updateTemplateData = (path: string, value: any) => {
    const keys = path.split('.');
    const newData = { ...templateData };
    let current = newData;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setTemplateData(newData);
  };

  const handleLogoUpload = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('certificate-logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('certificate-logos')
        .getPublicUrl(filePath);

      updateTemplateData('logo.url', data.publicUrl);
      updateTemplateData('logo.enabled', true);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleWatermarkUpload = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `watermark_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('certificate-logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('certificate-logos')
        .getPublicUrl(filePath);

      updateTemplateData('watermark.image', data.publicUrl);
      updateTemplateData('watermark.enabled', true);
      toast.success('Watermark uploaded successfully');
    } catch (error) {
      console.error('Error uploading watermark:', error);
      toast.error('Failed to upload watermark');
    }
  };

  const handleSave = async () => {
    const updatedTemplate: CertificateTemplate = {
      ...template,
      template_data: templateData,
      background_type: backgroundType,
      background_value: backgroundValue,
      logo_url: templateData.logo?.url || template.logo_url
    };

    onSave(updatedTemplate);
  };

  const generatePreview = () => {
    return (
      <div 
        className="relative w-full aspect-[8.5/11] mx-auto bg-white shadow-lg overflow-hidden animate-fade-in"
        style={{
          background: backgroundType === 'solid' ? backgroundValue : 
                     backgroundType === 'gradient' ? backgroundValue : 
                     `url(${backgroundValue}) center/cover`,
          border: templateData.border?.enabled ? 
            `${templateData.border.width}px ${templateData.border.style} ${templateData.border.color}` : 'none',
          borderRadius: templateData.border?.cornerRadius ? `${templateData.border.cornerRadius}px` : '0'
        }}
      >
        {/* Watermark */}
        {templateData.watermark?.enabled && templateData.watermark?.image && (
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: templateData.watermark.opacity }}
          >
            <img 
              src={templateData.watermark.image} 
              alt="Watermark" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}

        {/* Logo */}
        {templateData.logo?.enabled && templateData.logo?.url && (
          <div 
            className={`absolute ${
              templateData.logo.position === 'top-left' ? 'top-4 left-4' :
              templateData.logo.position === 'top-right' ? 'top-4 right-4' :
              templateData.logo.position === 'top-center' ? 'top-4 left-1/2 transform -translate-x-1/2' :
              'top-4 left-4'
            }`}
          >
            <img 
              src={templateData.logo.url} 
              alt="Logo" 
              className="object-contain"
              style={{ width: `${templateData.logo.size}px`, height: `${templateData.logo.size}px` }}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-4">
          <h1 
            className="font-serif"
            style={{
              fontSize: `${templateData.fonts?.title?.size || 32}px`,
              fontWeight: templateData.fonts?.title?.weight || 'bold',
              color: templateData.fonts?.title?.color || '#1e40af'
            }}
          >
            {templateData.title}
          </h1>
          
          <p 
            style={{
              fontSize: `${templateData.fonts?.subtitle?.size || 18}px`,
              color: templateData.fonts?.subtitle?.color || '#6b7280'
            }}
          >
            {templateData.subtitle}
          </p>

          <div className="py-4">
            <p className="text-lg text-gray-600 mb-2">This certifies that</p>
            <p 
              className="font-serif border-b-2 border-gray-300 pb-2 mb-4 min-w-64"
              style={{
                fontSize: `${templateData.fonts?.participant?.size || 28}px`,
                fontWeight: templateData.fonts?.participant?.weight || 'bold',
                color: templateData.fonts?.participant?.color || '#111827'
              }}
            >
              [Participant Name]
            </p>
            <p className="text-lg text-gray-600 mb-2">has successfully completed</p>
            <p 
              className="font-medium"
              style={{
                fontSize: `${templateData.fonts?.course?.size || 20}px`,
                color: templateData.fonts?.course?.color || '#374151'
              }}
            >
              [Course Title]
            </p>
          </div>

          {/* PD Hours */}
          {templateData.pdHours?.enabled && (
            <div className="text-sm text-gray-600">
              <p>{templateData.pdHours.label}: [X] Hours</p>
            </div>
          )}

          {/* Dates */}
          {(templateData.dates?.completion?.enabled || templateData.dates?.issued?.enabled) && (
            <div className="flex gap-8 text-sm text-gray-600">
              {templateData.dates?.completion?.enabled && (
                <div>
                  <p>{templateData.dates.completion.label}:</p>
                  <p className="border-b border-gray-300 min-w-24">__________</p>
                </div>
              )}
              {templateData.dates?.issued?.enabled && (
                <div>
                  <p>{templateData.dates.issued.label}:</p>
                  <p className="border-b border-gray-300 min-w-24">__________</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Signatures */}
        {templateData.signatures?.enabled && (
          <div className="absolute bottom-8 left-8 right-8 flex justify-between">
            {templateData.signatures?.instructor?.enabled && (
              <div className="text-center">
                <div className="border-b border-gray-400 w-48 mb-2"></div>
                <p className="text-sm text-gray-600">{templateData.signatures.instructor.label}</p>
              </div>
            )}
            {templateData.signatures?.admin?.enabled && (
              <div className="text-center">
                <div className="border-b border-gray-400 w-48 mb-2"></div>
                <p className="text-sm text-gray-600">{templateData.signatures.admin.label}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Design Controls */}
        <div className="space-y-4">
          <Tabs defaultValue="layout" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="layout">Layout</TabsTrigger>
              <TabsTrigger value="fonts">Fonts</TabsTrigger>
              <TabsTrigger value="border">Border</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="elements">Elements</TabsTrigger>
            </TabsList>

            <TabsContent value="layout" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Background
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Background Type</Label>
                    <Select value={backgroundType} onValueChange={setBackgroundType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Solid Color</SelectItem>
                        <SelectItem value="gradient">Gradient</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {backgroundType === 'solid' && (
                    <div>
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={backgroundValue}
                          onChange={(e) => setBackgroundValue(e.target.value)}
                          className="w-16 h-10"
                        />
                        <Input
                          value={backgroundValue}
                          onChange={(e) => setBackgroundValue(e.target.value)}
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>
                  )}

                  {backgroundType === 'gradient' && (
                    <div>
                      <Label>Gradient CSS</Label>
                      <Input
                        value={backgroundValue}
                        onChange={(e) => setBackgroundValue(e.target.value)}
                        placeholder="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={templateData.title}
                        onChange={(e) => updateTemplateData('title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Subtitle</Label>
                      <Input
                        value={templateData.subtitle}
                        onChange={(e) => updateTemplateData('subtitle', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="fonts" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Typography
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['title', 'subtitle', 'participant', 'course', 'details'].map((fontType) => (
                    <div key={fontType} className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-medium capitalize">{fontType} Font</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Size</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[templateData.fonts?.[fontType]?.size || 16]}
                              onValueChange={([value]) => updateTemplateData(`fonts.${fontType}.size`, value)}
                              min={10}
                              max={48}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-sm w-8">{templateData.fonts?.[fontType]?.size || 16}</span>
                          </div>
                        </div>
                        <div>
                          <Label>Color</Label>
                          <Input
                            type="color"
                            value={templateData.fonts?.[fontType]?.color || '#000000'}
                            onChange={(e) => updateTemplateData(`fonts.${fontType}.color`, e.target.value)}
                            className="w-full h-10"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="border" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Border Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={templateData.border?.enabled}
                      onCheckedChange={(checked) => updateTemplateData('border.enabled', checked)}
                    />
                    <Label>Enable Border</Label>
                  </div>

                  {templateData.border?.enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Border Color</Label>
                          <Input
                            type="color"
                            value={templateData.border?.color || '#1e40af'}
                            onChange={(e) => updateTemplateData('border.color', e.target.value)}
                            className="w-full h-10"
                          />
                        </div>
                        <div>
                          <Label>Border Width</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[templateData.border?.width || 4]}
                              onValueChange={([value]) => updateTemplateData('border.width', value)}
                              min={1}
                              max={20}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-sm w-6">{templateData.border?.width || 4}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label>Corner Radius</Label>
                        <div className="flex items-center gap-2">
                          <Slider
                            value={[templateData.border?.cornerRadius || 8]}
                            onValueChange={([value]) => updateTemplateData('border.cornerRadius', value)}
                            min={0}
                            max={50}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-sm w-6">{templateData.border?.cornerRadius || 8}</span>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="images" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Logo & Watermark
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Logo Section */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={templateData.logo?.enabled}
                        onCheckedChange={(checked) => updateTemplateData('logo.enabled', checked)}
                      />
                      <Label>Enable Logo</Label>
                    </div>

                    {templateData.logo?.enabled && (
                      <>
                        <div>
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                            className="w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Logo
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setLogoFile(file);
                                handleLogoUpload(file);
                              }
                            }}
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Position</Label>
                            <Select
                              value={templateData.logo?.position || 'top-left'}
                              onValueChange={(value) => updateTemplateData('logo.position', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="top-left">Top Left</SelectItem>
                                <SelectItem value="top-right">Top Right</SelectItem>
                                <SelectItem value="top-center">Top Center</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Size</Label>
                            <div className="flex items-center gap-2">
                              <Slider
                                value={[templateData.logo?.size || 80]}
                                onValueChange={([value]) => updateTemplateData('logo.size', value)}
                                min={40}
                                max={200}
                                step={10}
                                className="flex-1"
                              />
                              <span className="text-sm w-8">{templateData.logo?.size || 80}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <Separator />

                  {/* Watermark Section */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={templateData.watermark?.enabled}
                        onCheckedChange={(checked) => updateTemplateData('watermark.enabled', checked)}
                      />
                      <Label>Enable Watermark</Label>
                    </div>

                    {templateData.watermark?.enabled && (
                      <>
                        <div>
                          <Button
                            onClick={() => watermarkInputRef.current?.click()}
                            variant="outline"
                            className="w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Watermark
                          </Button>
                          <input
                            ref={watermarkInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setWatermarkFile(file);
                                handleWatermarkUpload(file);
                              }
                            }}
                          />
                        </div>

                        <div>
                          <Label>Opacity</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[templateData.watermark?.opacity || 0.1]}
                              onValueChange={([value]) => updateTemplateData('watermark.opacity', value)}
                              min={0.05}
                              max={0.5}
                              step={0.05}
                              className="flex-1"
                            />
                            <span className="text-sm w-12">{(templateData.watermark?.opacity || 0.1).toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="elements" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Signature className="h-4 w-4" />
                    Certificate Elements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* PD Hours */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={templateData.pdHours?.enabled}
                        onCheckedChange={(checked) => updateTemplateData('pdHours.enabled', checked)}
                      />
                      <Label>Show PD Hours</Label>
                    </div>
                    {templateData.pdHours?.enabled && (
                      <Input
                        value={templateData.pdHours?.label || 'Professional Development Hours'}
                        onChange={(e) => updateTemplateData('pdHours.label', e.target.value)}
                        placeholder="PD Hours Label"
                      />
                    )}
                  </div>

                  <Separator />

                  {/* Dates */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date Fields
                    </h4>
                    
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={templateData.dates?.completion?.enabled}
                          onCheckedChange={(checked) => updateTemplateData('dates.completion.enabled', checked)}
                        />
                        <Label>Completion Date</Label>
                      </div>
                      {templateData.dates?.completion?.enabled && (
                        <Input
                          value={templateData.dates?.completion?.label || 'Date Completed'}
                          onChange={(e) => updateTemplateData('dates.completion.label', e.target.value)}
                          placeholder="Completion Date Label"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={templateData.dates?.issued?.enabled}
                          onCheckedChange={(checked) => updateTemplateData('dates.issued.enabled', checked)}
                        />
                        <Label>Issue Date</Label>
                      </div>
                      {templateData.dates?.issued?.enabled && (
                        <Input
                          value={templateData.dates?.issued?.label || 'Date Issued'}
                          onChange={(e) => updateTemplateData('dates.issued.label', e.target.value)}
                          placeholder="Issue Date Label"
                        />
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Signatures */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={templateData.signatures?.enabled}
                        onCheckedChange={(checked) => updateTemplateData('signatures.enabled', checked)}
                      />
                      <Label>Enable Signature Lines</Label>
                    </div>

                    {templateData.signatures?.enabled && (
                      <>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={templateData.signatures?.instructor?.enabled}
                              onCheckedChange={(checked) => updateTemplateData('signatures.instructor.enabled', checked)}
                            />
                            <Label>Instructor Signature</Label>
                          </div>
                          {templateData.signatures?.instructor?.enabled && (
                            <Input
                              value={templateData.signatures?.instructor?.label || 'Instructor Signature'}
                              onChange={(e) => updateTemplateData('signatures.instructor.label', e.target.value)}
                              placeholder="Instructor Signature Label"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={templateData.signatures?.admin?.enabled}
                              onCheckedChange={(checked) => updateTemplateData('signatures.admin.enabled', checked)}
                            />
                            <Label>Administrator Signature</Label>
                          </div>
                          {templateData.signatures?.admin?.enabled && (
                            <Input
                              value={templateData.signatures?.admin?.label || 'Administrator'}
                              onChange={(e) => updateTemplateData('signatures.admin.label', e.target.value)}
                              placeholder="Administrator Signature Label"
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex gap-2">
            <Button onClick={handleSave} className="hover-scale">
              Save Template
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Live Preview
            </h3>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
          
          <div className="border rounded-lg p-4 bg-gray-50 overflow-auto max-h-[800px]">
            <div className="scale-75 origin-top">
              {generatePreview()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}